import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  EyeOff, Loader2, MessagesSquare, RefreshCw, Search,
} from 'lucide-react';
import client from '../lib/api';
import { useToast } from '../hooks/use-toast';
import ConfirmDialog from './components/ConfirmDialog';
import { AdminError } from './components/PageState';

// Oyuncu sohbetinin moderasyon ekranı.
//
// Menüde **yalnızca Herkül şeması yayındayken** görünür (AdminLayout'taki
// `theme: 'herkul'` bayrağı): sohbet rayını yalnızca o kabuk çizer, başka bir
// şemadayken oyuncunun göremediği bir akışı yönetmek anlamsızdır. Rota yine
// tanımlıdır — şema değiştirilip geri alındığında elle girilmiş bir bağlantı
// kırılmasın.
//
// Mesaj **silinmez, gizlenir**: moderasyon kararı geri alınabilmeli ve "kim
// ne yazmıştı" sorusu sonradan yanıtlanabilmelidir (bkz. services/chat.py).
// Bu yüzden liste gizlenmiş satırları da gösterir.

const FILTERS = [
  { id: 'all', label: 'Tümü' },
  { id: 'visible', label: 'Yayında' },
  { id: 'deleted', label: 'Gizlenmiş' },
];

const ChatModeration = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [term, setTerm] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await client.get('/admin/chat/messages');
      setItems(data.items || []);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Sohbet mesajları yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const needle = term.trim().toLocaleLowerCase('tr');
    return items.filter((item) => {
      if (filter === 'visible' && item.deleted) return false;
      if (filter === 'deleted' && !item.deleted) return false;
      if (!needle) return true;
      return `${item.username} ${item.body}`.toLocaleLowerCase('tr').includes(needle);
    });
  }, [items, filter, term]);

  const hideMessage = async () => {
    if (!confirmation) return;
    setBusy(true);
    try {
      await client.delete(`/admin/chat/messages/${confirmation.id}`);
      toast({ title: 'Mesaj gizlendi' });
      setConfirmation(null);
      await load();
    } catch (requestError) {
      toast({
        title: 'İşlem başarısız',
        description: requestError.response?.data?.detail,
      });
    } finally {
      setBusy(false);
    }
  };

  const hiddenCount = items.filter((item) => item.deleted).length;

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ background: 'rgba(228,187,108,0.15)', color: 'var(--v2-warn, #e4bb6c)' }}
        >
          <MessagesSquare size={18} />
        </div>
        <div>
          <h1 className="text-xl font-bold">Sohbet Moderasyonu</h1>
          <p className="text-xs text-slate-400">
            Herkül şemasındaki oyuncu sohbeti — son {items.length} mesaj
            {hiddenCount > 0 ? `, ${hiddenCount} tanesi gizli` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="icon-btn edit ml-auto"
          title="Yenile"
          disabled={loading}
        >
          <RefreshCw size={13} />
        </button>
      </div>

      <div className="flex gap-2 flex-wrap mb-4 items-center">
        {FILTERS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => setFilter(option.id)}
            className={`admin-tab ${filter === option.id ? 'active' : ''}`}
          >
            {option.label}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            className="admin-input pl-10 admin-input-w-md"
            placeholder="Üye adı veya mesajda ara"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
          />
        </div>
      </div>

      <div className="admin-card overflow-hidden">
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="inline animate-spin text-cyan-400" /></div>
        ) : error ? (
          <AdminError message={error} onRetry={load} />
        ) : visible.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            {items.length === 0 ? 'Henüz sohbet mesajı yok' : 'Aramaya uyan mesaj yok'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table w-full min-w-[760px]">
              <thead>
                <tr>
                  <th>ÜYE</th>
                  <th>MESAJ</th>
                  <th>TARİH</th>
                  <th>DURUM</th>
                  <th>İŞLEM</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((item) => (
                  <tr key={item.id} className={item.deleted ? 'opacity-60' : ''}>
                    <td className="font-semibold whitespace-nowrap">{item.username}</td>
                    <td className="max-w-[420px] break-words">{item.body}</td>
                    <td className="text-xs text-slate-400 whitespace-nowrap">
                      {item.created_at
                        ? new Date(`${item.created_at}Z`).toLocaleString('tr-TR')
                        : '—'}
                    </td>
                    <td>
                      <span className={`badge-pill ${item.deleted ? 'badge-red' : 'badge-green'}`}>
                        {item.deleted ? 'Gizli' : 'Yayında'}
                      </span>
                    </td>
                    <td>
                      {item.deleted ? (
                        <span className="text-xs text-slate-500">—</span>
                      ) : (
                        <button
                          type="button"
                          title="Mesajı gizle"
                          onClick={() => setConfirmation(item)}
                          className="icon-btn ban"
                        >
                          <EyeOff size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(confirmation)}
        title="Mesajı gizle"
        description={confirmation
          ? `${confirmation.username} adlı üyenin mesajı sohbetten kaldırılacak. `
            + 'Kayıt silinmez; bu ekranda "Gizli" olarak görünmeye devam eder.'
          : ''}
        confirmLabel="Gizle"
        tone="danger"
        busy={busy}
        onCancel={() => !busy && setConfirmation(null)}
        onConfirm={hideMessage}
      />
    </div>
  );
};

export default ChatModeration;
